import { isEqual } from 'lodash'
import {secondsDiff} from '@/utils/datetime'

export const mutations = {
  SET_FEATURES(state, payload) {
    const { features, isMerge } = payload

    const newFeatures = features.features.map(f => {
      const { resources } = f.properties
      // Add thumbUrl to feature for easier access in layer API
      const thumbUrl = resources?.find(r => r.url?.includes('small'))?.url || resources?.find(r => r.url?.includes('thumbnail'))?.url
      if (thumbUrl) {
        f.properties.thumbUrl = thumbUrl
      }
      return f
    })

    state.features = {
      type: 'FeatureCollection',
      features: newFeatures,
    }
  },

  MERGE_FEATURES(state, payload) {
    const { features } = payload

    const newFeatures = features.features.map(f => {
      const { resources } = f.properties
      const thumbUrl = resources?.find(r => r.url?.includes('small'))?.url || resources?.find(r => r.url?.includes('thumbnail'))?.url
      if (thumbUrl) {
        f.properties.thumbUrl = thumbUrl
      }

      // Restore previously loaded geometry for heavy features
      const code = f.properties.code
      if (f.properties.is_heavy && !f.geometry && state.loadedHeavyGeometries[code]) {
        f.geometry = state.loadedHeavyGeometries[code]
      }

      return f
    })

    // Skip update only if feature codes and contents haven't changed
    const oldCodes = state.features.features.map(f => f.properties.code)
    const newCodes = newFeatures.map(f => f.properties.code)
    const sameCodes =
      oldCodes.length === newCodes.length &&
      oldCodes.every((code, i) => code === newCodes[i])
    if (sameCodes) {
      const hasDifferences = state.features.features.some((oldFeature, i) => {
        return !isEqual(oldFeature, newFeatures[i])
      })
      if (!hasDifferences) {
        return
      }
    }

    state.features = {
      type: 'FeatureCollection',
      features: newFeatures,
    }
  },

  SET_FEED_FEATURES(state, payload) {
    const { features } = payload
    const seen = new Set()
    const uniqueFeatures = features.features.filter(f => {
      const code = f.properties?.code
      if (!code || seen.has(code)) return false
      seen.add(code)
      return true
    })

    state.feedFeatures = {
      type: 'FeatureCollection',
      features: uniqueFeatures
    }
  },

  DELETE_FEATURE (state, code) {
    state.features = {
      type: 'FeatureCollection',
      features: state.features.features.filter(f => f.properties.code !== code)
    }

    state.feedFeatures = {
      type: 'FeatureCollection',
      features: state.feedFeatures.features.filter(f => f.properties.code !== code)
    }
  },

  SET_CURRENT_FEATURE  (state, payload) {
    state.currentFeature = payload.feature
  },
  SET_CURRENT_FEATURES_CHILDS(state, payload) {
    state.currentFeatureChilds = payload
  },

  CLEAR_SEARCH_SELECTED(state) {
    state.searchSelected = []
  },

  SET_MAP(state, payload) {
    state.map = payload
  },
  SET_MAP2(state, payload) {
    state.map2 = payload
  },
  SET_COMPARE_MODE(state, payload) {
    state.isCompareMode = payload
  },
  SET_MAP_READY(state, payload) {
    state.mapReady = payload
  },
  SET_MAP2_READY(state, payload) {
    state.map2Ready = payload
  },
  SET_DRAW(state, payload) {
    state.draw = payload
  },

  SET_BEACONS(state, payload) {
    state.beacons = payload
  },
  NEXT_BEACON(state, next_beacon) {
    if (!state.beacons || state.beacons.find(b => b.properties.member_id === next_beacon.properties.member_id)) {
      const index = state.beacons.findIndex(b => b.properties.member_id === next_beacon.properties.member_id)
      state.beacons.splice(index, 1, next_beacon)
    } else {
      state.beacons.push(next_beacon)
    }
  },
  REMOVE_BEACON(state, memberId) {
    const index = state.beacons.findIndex(beacon => beacon.properties.member_id === memberId)
    state.beacons.splice(index, 1)
  },
  CLEANUP_BEACONS(state) {
    const day = 24 * 60 * 60 * 1000
    state.beacons = state.beacons.filter(beacon => secondsDiff(new Date(), beacon.properties.rel_ts) <= day)
    console.log('WS: cleanup beacons')
  },

  SET_NOTES(state, payload) {
    state.notes = payload
  },
  ADD_NOTE(state, payload) {
    state.notes.push(payload)
  },
  DELETE_NOTE(state, noteId) {
    state.notes = state.notes.filter(n => n.id !== noteId)
  },

  SET_LOCATION(state, location) {
    if(location === null) {
      state.userSettings.location.lat = null
      state.userSettings.location.lng = null
    } else {
      state.userSettings.location.lat = location.coords.latitude
      state.userSettings.location.lng = location.coords.longitude
    }
  },

  SET_LAYERS(state, payload) {
    state.layers = payload
  },

  SET_FILES (state, payload) {
    state.files = payload
  },

  SET_TOTAL_ONMAP(state, payload) {
    state.totalOnMap = payload
  },

  ADD_REPORT_TEMPLATE(state, payload) {
    state.reportTemplates.push(payload)
  },

  SET_REPORT_TEMPLATES(state, payload) {
    state.reportTemplates = payload
  },

  SET_ALL_TEMPLATES(state, payload) {
    state.allTemplates = payload
  },

  UPDATE_REPORT_TEMPLATE(state, payload) {
    const index = state.reportTemplates.findIndex(t => t.id === payload.id)
    state.reportTemplates.splice(index, 1, payload)
  },

  DELETE_REPORT_TEMPLATE(state, payload) {
    state.reportTemplates = state.reportTemplates.filter(t => t.id !== payload.id)
  },

  SET_SYSTEM_TEMPLATES_LIBRARY(state, payload) {
    state.systemTemplatesLibrary = payload
  },

  REMOVE_FROM_LIBRARY(state, templateId) {
    if (state.systemTemplatesLibrary) {
      state.systemTemplatesLibrary = state.systemTemplatesLibrary.filter(t => t.id !== templateId)
    }
  },

  ADD_TO_LIBRARY(state, template) {
    if (state.systemTemplatesLibrary) {
      state.systemTemplatesLibrary.push(template)
    } else {
      state.systemTemplatesLibrary = [template]
    }
  },

  SET_FEATURE_CREATED(state, payload) {
    state.isFeatureCreated = payload
  },

  TOGGLE_FEATURE_VISIBILITY(state, featureCode) {
    if (state.featureVisibility[featureCode] === undefined || state.featureVisibility[featureCode] === true) {
      // If not set or visible, hide it
      state.featureVisibility = { ...state.featureVisibility, [featureCode]: false }
    } else {
      // If hidden, show it
      state.featureVisibility = { ...state.featureVisibility, [featureCode]: true }
    }
  },

  SET_FEATURE_VISIBILITY(state, { featureCode, visible }) {
    state.featureVisibility = { ...state.featureVisibility, [featureCode]: visible }
  },

  RESET_FEATURE_VISIBILITY(state) {
    state.featureVisibility = {}
  },

  SET_RESOURCES(state, resources) {
    state.resources = resources
  },

  ADD_RESOURCE(state, resource) {
    if (!state.resources) {
      state.resources = []
    }
    state.resources.push(resource)
  },

  REMOVE_RESOURCE(state, resourceId) {
    if (state.resources) {
      state.resources = state.resources.filter(r => !(r.uuid === resourceId || r.id === resourceId))
    }
  },

  UPDATE_RESOURCE(state, { resourceId, updates }) {
    if (state.resources) {
      const index = state.resources.findIndex(r => r.uuid === resourceId || r.id === resourceId)
      if (index !== -1) {
        state.resources[index] = { ...state.resources[index], ...updates }
      }
    }
  },

  SET_HEAVY_FEATURE_GEOMETRY(state, { code, geometry }) {
    state.loadedHeavyGeometries = { ...state.loadedHeavyGeometries, [code]: geometry }

    // Update the feature in the map features collection so the renderer picks it up
    const featureIndex = state.features.features.findIndex(f => f.properties.code === code)
    if (featureIndex !== -1) {
      const updatedFeature = { ...state.features.features[featureIndex], geometry }
      const updatedFeatures = [...state.features.features]
      updatedFeatures[featureIndex] = updatedFeature
      state.features = { type: 'FeatureCollection', features: updatedFeatures }
    }
  },

  SET_HEAVY_FEATURE_LOADING(state, { code, loading }) {
    state.heavyFeatureLoading = { ...state.heavyFeatureLoading, [code]: loading }
  },

  CLEAR_HEAVY_GEOMETRIES(state) {
    state.loadedHeavyGeometries = {}
    state.heavyFeatureLoading = {}
  },

  SET_PROJECT_FILTERS(state, filters) {
    state.projectFilters = filters
  },
}
